import { BaseClient } from './baseClient';
import { APIResponse } from '../types';
import { OutreachAction } from '../components/forms/outreach/types';

/** One read of the analyst's board: the cards, plus whether more are coming. */
export interface OutreachActionsRead {
    actions: OutreachAction[];
    /** Robbie is still writing opening asks for suppliers that have none yet. */
    kickoffPending: boolean;
}

class OutreachClient extends BaseClient {
    async sendSupplierOutreach(
        id: string,
        componentId: string,
        // Compose and dispatch in one call: the buyer submits a query, the
        // backend writes the email from it. No address field by design — the
        // recipient is resolved from the stored supplier candidate — and no
        // subject/body/finalContent, which the backend rejects for supplier
        // stages so no buyer-typed text can reach a supplier verbatim.
        payload: {
            supplierId: string;
            supplierName?: string;
            /** 'NEGOTIATION' sends a counter-offer (requires context.buyerAsk); 'RFQ' opens one. */
            stage: 'RFQ' | 'NEGOTIATION';
            context: any;
            /** The buyer's ask in their own words — stored on the message. */
            sourceQuery: string;
        }
    ): Promise<any> {
        const response = await this.api.post(`/trp/rfqs/${id}/components/${componentId}/outreach/send`, payload);
        if (response.data.success) {
            return response.data;
        } else {
            throw new Error(response.data.error || 'Outreach request failed');
        }
    }

    /**
     * The buyer's answer to a supplier offering a different spec than the BOM:
     * ASK_FOR_OURS keeps outreach asking for the BOM's spec, ACCEPT_THEIRS lets
     * the supplier's stand.
     */
    async setSpecDecision(
        id: string,
        componentId: string,
        payload: { supplierId?: string; supplierName: string; decision: 'ASK_FOR_OURS' | 'ACCEPT_THEIRS' }
    ): Promise<void> {
        const response = await this.api.patch(`/trp/rfqs/${id}/components/${componentId}/outreach/spec-decision`, payload);
        if (!response.data.success) {
            throw new Error(response.data.message || response.data.error || 'Spec decision failed');
        }
    }

    /**
     * The thread analyst's open moves for this RFQ, org-scoped and best move
     * first. Defaults to OPEN server-side; resolved actions are history.
     *
     * `kickoffPending` says Robbie is still working out opening asks for a
     * supplier nobody has written to — without it, a thread with no card is
     * indistinguishable from one there was nothing to say about.
     */
    async listOutreachActions(
        id: string,
        status?: 'OPEN' | 'DONE' | 'DISMISSED' | 'STALE' | 'ALL'
    ): Promise<OutreachActionsRead> {
        const response = await this.api.get<APIResponse<OutreachAction[]> & { meta?: { kickoffPending?: boolean } }>(
            `/trp/rfqs/${id}/outreach/actions`,
            { params: status ? { status } : undefined }
        );
        if (response.data.success && response.data.data) {
            return {
                actions: response.data.data,
                kickoffPending: response.data.meta?.kickoffPending === true,
            };
        }
        return { actions: [], kickoffPending: false };
    }

    /**
     * Closes one action: DONE when the buyer acted on it, DISMISSED when they
     * judged it not worth doing. Both survive a recompute, which is what stops
     * the same suggestion coming back tomorrow.
     */
    async resolveOutreachAction(id: string, actionId: string, status: 'DONE' | 'DISMISSED'): Promise<OutreachAction> {
        const response = await this.api.patch<APIResponse<OutreachAction>>(
            `/trp/rfqs/${id}/outreach/actions/${actionId}`,
            { status }
        );
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        throw new Error(response.data.error || 'Failed to update the outreach action');
    }

    async getThreadMessages(id: string, componentId: string, threadId: string): Promise<any[]> {
        const response = await this.api.get<APIResponse<any[]>>(`/trp/rfqs/${id}/components/${componentId}/outreach/threads/${threadId}/messages`);
        if (response.data.success && response.data.data) {
            return response.data.data;
        }
        return [];
    }
}

export const outreachClient = new OutreachClient();
